import { useState, useEffect } from 'react';
import { getAllCountries } from '../../../services/firestoreService';
import { initializeCountryRegistry } from '../../../services/userService';
import Table from '../../shared/UI/Table';
import Badge from '../../shared/UI/Badge';
import Button from '../../shared/UI/Button';
import ToggleField from '../../shared/Forms/ToggleField';

export default function CountryList({ onAdd, onEdit }) {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    try {
      // Always call initializeCountryRegistry — it only adds missing defaults
      setSeeding(true);
      await initializeCountryRegistry().catch(() => {});
      setSeeding(false);
      const list = await getAllCountries();
      setCountries(list);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setSeeding(false);
    }
  }

  useEffect(() => { load(); }, []);

  const displayed = showInactive ? countries : countries.filter(c => c.is_active !== false);

  const columns = [
    { key: 'name', label: 'Country Name' },
    { key: 'id', label: 'ISO Code' },
    { key: 'currency_name', label: 'Currency Name' },
    { key: 'currency_code', label: 'Currency Code' },
    { key: 'currency_symbol', label: 'Symbol' },
    {
      key: 'legal_framework',
      label: 'Legal Framework',
      render: row => row.legal_framework?.replace('_', ' ') || '—',
    },
    { key: 'region', label: 'Region' },
    {
      key: 'is_active',
      label: 'Status',
      render: row => <Badge text={row.is_active !== false ? 'active' : 'inactive'} />,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: row => (
        <Button size="sm" variant="secondary" onClick={() => onEdit(row)}>Edit</Button>
      ),
    },
  ];

  if (error) return <div className="alert alert-error">{error}</div>;

  if (seeding) {
    return (
      <div className="card">
        <div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
          <div className="spinner" style={{ margin: '0 auto 12px' }} />
          <p style={{ color: 'var(--color-text-secondary)' }}>Seeding default countries…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2>Countries & Currencies ({displayed.length})</h2>
        <div className="flex gap-3">
          <ToggleField label="Show inactive" checked={showInactive} onChange={setShowInactive} />
          <Button variant="primary" onClick={onAdd}>+ Add Country</Button>
        </div>
      </div>
      <Table
        columns={columns}
        data={displayed}
        loading={loading}
        emptyTitle="No countries in registry"
        emptyMessage="Add countries to the registry."
        emptyAction={{ label: '+ Add Country', onClick: onAdd }}
      />
    </div>
  );
}
